import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LoadingState } from '@v2/design-system';
import {
    PiCaretLeftBold,
    PiCaretRightBold,
    PiTrophyBold,
    PiWarningBold,
} from 'react-icons/pi';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import type { CommissionGoal } from '@v2/pages/commissioning/types';

const BUCKET_STROKE: Record<string, string> = {
    force: '#3b82f6',
    cardio: '#ef4444',
    gluteleader: '#a855f7',
    accessories: '#22c55e',
};

function BucketDonutMini({ bucket, label, pctRaw, achieved }: {
    bucket: string;
    label: string;
    pctRaw: number;
    achieved: number;
}) {
    const size = 72;
    const stroke = 7;
    const r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const dash = Math.min(1, pctRaw) * circ;
    const color = BUCKET_STROKE[bucket] ?? '#94a3b8';
    const pctDisplay = (pctRaw * 100).toFixed(0);
    const achievedStr = achieved >= 1_000_000
        ? `R$ ${(achieved / 1_000_000).toFixed(1)}M`
        : achieved >= 1_000 ? `R$ ${(achieved / 1_000).toFixed(0)}k` : `R$ 0`;

    return (
        <div className="flex flex-col items-center gap-1">
            <div className="relative" style={{ width: size, height: size }}>
                <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={r}
                        fill="none"
                        stroke={color}
                        strokeWidth={stroke}
                        strokeDasharray={`${dash} ${circ}`}
                        strokeLinecap="round"
                        style={{ transition: 'stroke-dasharray .5s ease' }}
                    />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-bold text-gray-800 dark:text-white">{pctDisplay}%</span>
                </div>
            </div>
            <span className="text-[10px] font-medium text-gray-600 dark:text-gray-300">{label}</span>
            <span className="text-[9px] text-gray-400">{achievedStr}</span>
        </div>
    );
}

function bucketStatusKey(pct: number): 'hit' | 'near' | 'far' {
    if (pct >= 1) return 'hit';
    if (pct >= 0.8) return 'near';
    return 'far';
}

type Props = {
    userId?: string;
    /** Sem setas de mês (home simples). */
    compact?: boolean;
    /** Mês controlado pelo parent (YYYY-MM). */
    period?: string;
};

export default function SellerBucketGoalsCard({ userId, compact = false, period: periodProp }: Props) {
    const { t, locale } = useI18n();
    const dateLocale = locale === 'pt_BR' ? 'pt-BR' : locale === 'zh_CN' ? 'zh-CN' : locale === 'es' ? 'es' : 'en-US';
    const now = new Date();
    const [year, setYear] = useState(() => {
        if (periodProp) return Number(periodProp.split('-')[0]);
        return now.getFullYear();
    });
    const [month, setMonth] = useState(() => {
        if (periodProp) return Number(periodProp.split('-')[1]);
        return now.getMonth() + 1;
    });

    useEffect(() => {
        if (!periodProp) return;
        const [y, m] = periodProp.split('-').map(Number);
        if (y && m) {
            setYear(y);
            setMonth(m);
        }
    }, [periodProp]);

    const period = `${year}-${String(month).padStart(2, '0')}`;
    const isSelf = !userId;
    const showNav = !compact && !periodProp;

    function prev() {
        if (month === 1) {
            setYear((y) => y - 1);
            setMonth(12);
        } else {
            setMonth((m) => m - 1);
        }
    }

    function next() {
        if (month === 12) {
            setYear((y) => y + 1);
            setMonth(1);
        } else {
            setMonth((m) => m + 1);
        }
    }

    const { data, isLoading } = useQuery<{ data: CommissionGoal | CommissionGoal[] | null }>({
        queryKey: ['commissioning', 'goals', period, userId ?? 'self'],
        queryFn: () => isSelf
            ? api.get('/commissioning/goals/my-performance', { params: { period } }).then((r) => r.data)
            : api.get('/commissioning/goals', { params: { period, user_id: userId } }).then((r) => r.data),
    });

    const goal = Array.isArray(data?.data)
        ? (data.data.find((g) => String(g.user_id) === String(userId)) ?? data.data[0] ?? null)
        : data?.data ?? null;
    const perf = goal?.performance;
    const periodLabel = new Date(year, month - 1, 1).toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' });
    const capLabel = periodLabel.charAt(0).toUpperCase() + periodLabel.slice(1);
    const globalStatus = perf ? bucketStatusKey(perf.pct_global) : null;

    return (
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
            <div className="border-b border-gray-100 px-4 py-3 dark:border-zinc-800">
                <div className="flex items-center justify-between gap-2">
                    <div>
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                            {t('v2.my_commission.buckets_title')}
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-zinc-400">
                            {t('v2.my_commission.buckets_subtitle', { period: capLabel })}
                        </p>
                    </div>
                    {showNav && (
                        <div className="flex items-center gap-1">
                            <button type="button" onClick={prev} className="rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-zinc-700">
                                <PiCaretLeftBold className="size-4 text-gray-500" />
                            </button>
                            <button type="button" onClick={next} className="rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-zinc-700">
                                <PiCaretRightBold className="size-4 text-gray-500" />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="px-4 py-3">
                {isLoading && <LoadingState variant="inline" className="py-6" />}
                {!isLoading && !goal && (
                    <p className="py-6 text-center text-xs text-gray-400">
                        {t('v2.my_commission.buckets_empty')}
                    </p>
                )}
                {goal && perf && (
                    <>
                        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-gray-50 px-4 py-2.5 dark:bg-zinc-800">
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                    {t('v2.my_commission.buckets_global')}
                                </p>
                                <p className="text-sm font-bold text-gray-900 dark:text-white">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(goal.global_target)}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                    {t('v2.my_commission.buckets_progress')}
                                </p>
                                <p className={cn(
                                    'text-lg font-black',
                                    globalStatus === 'hit' && 'text-emerald-600',
                                    globalStatus === 'near' && 'text-amber-600',
                                    globalStatus === 'far' && 'text-gray-700 dark:text-gray-200',
                                )}>
                                    {(perf.pct_global * 100).toFixed(1)}%
                                </p>
                                <p className="text-[10px] text-gray-500">
                                    {globalStatus === 'hit'
                                        ? t('v2.my_commission.buckets_status_hit')
                                        : globalStatus === 'near'
                                            ? t('v2.my_commission.buckets_status_near')
                                            : t('v2.my_commission.buckets_status_far')}
                                </p>
                            </div>
                            <div>
                                {(perf.is_exempt_spiff || goal.is_exempt_spiff) ? (
                                    <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500 dark:bg-zinc-700">
                                        {t('v2.my_commission.buckets_exempt')}
                                    </span>
                                ) : perf.earns_bonus ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
                                        <PiTrophyBold className="size-3" />
                                        {t('v2.my_commission.buckets_spiff_ok')}
                                    </span>
                                ) : perf.reached_100 ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-700">
                                        <PiWarningBold className="size-3" />
                                        {t('v2.my_commission.buckets_spiff_fc_only')}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                        <div className="flex flex-wrap justify-around gap-2">
                            {perf.buckets
                                .filter((b) => b.bucket !== 'other')
                                .map((b) => (
                                    <BucketDonutMini
                                        key={b.bucket}
                                        bucket={b.bucket}
                                        label={b.label}
                                        pctRaw={b.pct}
                                        achieved={b.achieved}
                                    />
                                ))}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
